<template>
    <BaseDialog v-model:open="isOpen" title="Create feedback request" dialog-content-class="overflow-y-hidden" data-testid="feedback-modal">
        <!-- List View -->
        <FeedbackModalContainer v-if="!isInCreationMode && !isInSharingMode && showListView">
            <FeedbackModalHeader title="Welcome to Feedback" sub-title="Collect focused feedback in three simple steps." @close="handleClose">
                <template #actions>
                    <BaseButton theme="primary" @click="goToCreationMode">
                        <div class="flex items-center gap-2">
                            <i class="bi bi-plus-lg font-bold" aria-hidden="true"></i>
                            <span class="body-small">New Request</span>
                        </div>
                    </BaseButton>
                </template>
            </FeedbackModalHeader>
            <FeedbackModalBody>
                <div class="flex flex-col justify-start gap-5 w-full h-full">
                    <!-- Three Steps Overview -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <FeedbackStepCard
                            icon="bi-pencil"
                            title="Step One: Craft a Question"
                            description="Choose a question about an area you want feedback on and share the link." />
                        <FeedbackStepCard
                            icon="bi-send"
                            title="Step Two: Nadia Collects Feedback"
                            description="Nadia chats with link recipients to collect and synthesize their feedback. All responses are fully anonymous." />
                        <FeedbackStepCard
                            icon="bi-rocket"
                            title="Step Three: Review, Reflect, Grow"
                            :description="`Once you've gotten at least ${minimumResponseCount} ${minimumResponseCount === 1 ? 'response' : 'responses'}, Nadia synthesizes the feedback and helps turn insights into growth.`" />
                    </div>

                    <!-- Existing Requests Heading -->
                    <h2 v-if="feedbackQuestions && feedbackQuestions.length > 0" class="title-body text-gray-700 mb-4">Existing Feedback Requests</h2>

                    <FeedbackRequestCard
                        v-for="feedback in feedbackQuestions"
                        :key="feedback.id"
                        :feedback="feedback"
                        :button-text="buttonText"
                        :url-button-text="urlButtonText"
                        :minimum-response-count="minimumResponseCount"
                        @delete="(id) => $emit('delete', id)"
                        @copy-link="(id) => $emit('copy-link', id, 'list')"
                        @copy-url="(id) => $emit('copy-url', id, 'list')"
                        @close-and-review="(feedback) => $emit('close-and-review', feedback)"
                        @view-report="(feedback) => $emit('view-report', feedback)"
                        @reopen="(feedback) => $emit('reopen', feedback)" />
                </div>
            </FeedbackModalBody>
        </FeedbackModalContainer>

        <!-- Creation View -->
        <FeedbackModalContainer v-else-if="isInCreationMode && !isInSharingMode">
            <FeedbackModalHeader title="Feedback" :show-back-button="showListView" @close="handleClose" @back="goBackToList" />

            <FeedbackModalBody>
                <!-- Essential Questions -->
                <template v-for="template in QUESTION_TEMPLATES" :key="template.name">
                    <div class="mb-16">
                        <h3 class="body-small text-gray-600 mb-4">{{ template.name }}</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                                v-for="question in template.questions"
                                :key="question"
                                data-testid="feedback-template-question"
                                class="px-4 py-8 border-2 border-gray-200 rounded-lgr cursor-pointer hover:border-purple-300 transition-colors"
                                @click="createFromTemplate(question)">
                                <p class="text-gray-800 title-body">{{ question }}</p>
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Creation Options -->
                <div class="mb-6">
                    <h3 class="body-small text-gray-600 mb-4">Or, create your own question</h3>

                    <!-- Action Buttons -->
                    <div v-if="!showManualInput" class="flex flex-col sm:flex-row gap-3 mb-6">
                        <!-- Create with Nadia - Preferred Option -->
                        <BaseButton theme="primary" class="flex-1" @click="goToChatCreation">
                            <div class="flex items-center gap-2 whitespace-nowrap justify-center">
                                <div class="w-5 h-5">
                                    <CoachingModeMarble size="small" />
                                </div>
                                Create with Nadia
                            </div>
                        </BaseButton>

                        <!-- Manual Creation -->
                        <BaseButton theme="secondary" class="flex-1" @click="showManualInput = true"> Create manually </BaseButton>
                    </div>

                    <!-- Manual Input (conditionally shown) -->
                    <div v-if="showManualInput" class="border-2 border-gray-200 rounded-lgr p-4">
                        <BaseTextarea
                            v-model="customQuestion"
                            aria-label="custom-question"
                            placeholder="Type your question here"
                            class="w-full min-h-25 resize-none border-none focus:outline-hidden text-gray-800 placeholder-gray-400"
                            :class="{ 'border-red-500': customQuestionError }"
                            maxlength="500"></BaseTextarea>

                        <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                            <div class="flex items-center gap-3">
                                <BaseButton
                                    theme="neutral"
                                    @click="
                                        showManualInput = false;
                                        customQuestion = '';
                                        customQuestionError = '';
                                    ">
                                    Cancel
                                </BaseButton>
                            </div>
                            <div class="flex items-center gap-4">
                                <span v-if="customQuestionError" class="text-red-600 body-small">{{ customQuestionError }}</span>
                                <span class="text-gray-400 body-small">{{ customQuestion.length }}/500</span>
                                <BaseButton theme="primary" :disabled="!customQuestion.trim() || customQuestion.length < 10 || isCreating" @click="createFromCustom">
                                    {{ isCreating ? "Creating..." : "Create Request" }}
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </div>
            </FeedbackModalBody>

            <!-- Loading State -->
            <div v-if="isCreating" class="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    <span class="text-gray-800">Creating your feedback question...</span>
                </div>
            </div>
        </FeedbackModalContainer>

        <!-- Sharing View -->
        <FeedbackModalContainer v-else-if="isInSharingMode">
            <FeedbackModalHeader title="Feedback" :show-back-button="showListView" @close="handleClose" @back="goBackToList" />

            <FeedbackModalBody>
                <!-- Feedback Request Card -->
                <div class="border-2 border-purple-300 rounded-lg p-6 mb-8">
                    <div class="flex justify-between items-start mb-4">
                        <h3 class="text-gray-600 body-semibold">Your feedback request</h3>
                    </div>

                    <div class="mb-6">
                        <h4 class="title-body text-gray-800 mb-4">{{ newlyCreatedQuestion?.question }}</h4>
                    </div>

                    <div class="grid justify-items-end">
                        <div class="flex gap-2">
                            <BaseButton data-testid="feedback-copy-link-button" theme="tertiary" @click="$emit('copy-link', newlyCreatedQuestion?.id, 'post_creation')">
                                {{ buttonText }}
                            </BaseButton>
                            <BaseButton data-testid="feedback-copy-url-button" theme="tertiary" @click="$emit('copy-url', newlyCreatedQuestion?.id, 'post_creation')">
                                {{ urlButtonText }}
                            </BaseButton>
                        </div>
                    </div>
                </div>

                <!-- What's Next Section -->
                <h2 class="title-body text-gray-800 mb-4">What's next?</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FeedbackStepCard
                        icon="bi-share"
                        title="Share with your team"
                        description="Copy the link above and share it with colleagues, teammates, or anyone whose perspective you value. They'll have a quick, anonymous conversation with Nadia about you." />
                    <FeedbackStepCard
                        icon="bi-envelope"
                        title="Get notified when ready"
                        description="You'll receive an email notification once your feedback request has collected enough responses and is ready for you to review and discuss with Nadia." />
                </div>
            </FeedbackModalBody>
        </FeedbackModalContainer>
    </BaseDialog>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import FeedbackModalBody from "~vue/Feedback/FeedbackModalBody.vue";
import FeedbackModalContainer from "~vue/Feedback/FeedbackModalContainer.vue";
import FeedbackModalHeader from "~vue/Feedback/FeedbackModalHeader.vue";
import FeedbackRequestCard from "~vue/Feedback/FeedbackRequestCard.vue";
import FeedbackStepCard from "~vue/Feedback/FeedbackStepCard.vue";
import { openUrl } from "~vue/utils";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref, watch } from "vue";

const props = defineProps({
    open: { type: Boolean, default: false },
    showListView: { type: Boolean, default: true },
    feedbackQuestions: { type: Array, default: () => [] },
    buttonText: { type: String, default: "Copy Invite" },
    urlButtonText: { type: String, default: "Copy URL" },
    minimumResponseCount: { type: Number, default: 3 },
});

const emit = defineEmits(["update:open", "delete", "copy-link", "copy-url", "close-and-review", "view-report", "reopen", "question-created"]);

const coachingSessionUuid = inject("coachingSessionUuid", null);

const isOpen = computed({
    get: () => props.open,
    set: (value) => emit("update:open", value),
});

// Modal state management
const isInCreationMode = ref(false);
const isInSharingMode = ref(false);
const customQuestion = ref("");
const customQuestionError = ref("");
const isCreating = ref(false);
const showManualInput = ref(false);
const newlyCreatedQuestion = ref(null);

// Question templates for creation mode
const QUESTION_TEMPLATES = [
    {
        name: "Essential questions",
        questions: [
            "How effectively do I communicate my ideas and expectations?",
            "How well do I collaborate across different teams?",
            "How approachable am I when others need help or have questions?",
            "How well-equipped am I with the skills needed for my current role?",
        ],
    },
    {
        name: "Thought-provoking questions",
        questions: [
            "How well do I balance being supportive with holding others accountable?",
            "How effectively do I contribute to creating an inclusive team environment?",
            "How well do I handle feedback and adapt my approach when needed?",
            "How effectively do I manage priorities and help the team stay focused?",
        ],
    },
];

// Modal management functions
const goToCreationMode = () => {
    logUserInteraction("feedback_gathering", {}, coachingSessionUuid);
    isInCreationMode.value = true;
};

const resetModalState = () => {
    isInCreationMode.value = false;
    isInSharingMode.value = false;
    customQuestion.value = "";
    customQuestionError.value = "";
    newlyCreatedQuestion.value = null;
    isCreating.value = false;
    showManualInput.value = false;
};

const goBackToList = () => {
    resetModalState();
};

const handleClose = () => {
    isOpen.value = false;
    resetModalState();
};

const goToSharingMode = (question) => {
    isInCreationMode.value = false;
    isInSharingMode.value = true;
    newlyCreatedQuestion.value = question;
};

const createFromTemplate = async (questionText) => {
    await createFeedbackQuestion(questionText, "template");
};

const createFromCustom = async () => {
    if (!customQuestion.value.trim()) {
        customQuestionError.value = "Please enter a question";
        return;
    }

    if (customQuestion.value.length < 10) {
        customQuestionError.value = "Question must be at least 10 characters";
        return;
    }

    customQuestionError.value = "";
    await createFeedbackQuestion(customQuestion.value.trim(), "manual");
};

const createFeedbackQuestion = async (questionText, creationMethod) => {
    isCreating.value = true;
    customQuestionError.value = "";

    try {
        logUserInteraction(
            "feedback_question_created",
            {
                creation_method: creationMethod,
                question_length: questionText.length,
            },
            coachingSessionUuid,
        );

        // Create a new useFetch instance for this specific request
        const { execute, data, statusCode } = useFetch("/api/accounts/feedback-questions", { immediate: false })
            .post({
                question: questionText,
                creation_method: creationMethod,
            })
            .json();

        await execute();

        if (statusCode.value === 400 && data.value?.question) {
            customQuestionError.value = Array.isArray(data.value.question) ? data.value.question[0] : data.value.question;
            return;
        }

        // Notify parent that a question was created
        emit("question-created", data.value);

        // Go to sharing view
        goToSharingMode(data.value);
    } catch (error) {
        console.error("Error creating feedback question:", error);
    } finally {
        isCreating.value = false;
    }
};

const goToChatCreation = () => {
    logUserInteraction("feedback_chat_selected", {}, coachingSessionUuid);
    openUrl("coach/start/?script=feedback_create_request", {
        queryParams: { chat_source: CHATSOURCE.HOME_USER_FEEDBACK },
    });
};

// Watch for modal open to set initial state
watch(
    () => props.open,
    (newValue) => {
        if (newValue) {
            // If not showing list view, go directly to creation mode
            if (!props.showListView) {
                isInCreationMode.value = true;
            }
        } else {
            // Modal was closed, reset all state
            resetModalState();
        }
    },
);
</script>
